export const STORY_BATTLE_RARITY_XP = Object.freeze({
  common: 25,
  uncommon: 50,
  rare: 100,
  epic: 200,
  legendary: 300,
});

export const STORY_BATTLE_CARD_PLAY_XP = 5;
export const STORY_BATTLE_BOSS_XP = 100;

const INELIGIBLE_RESULT_KEYS = new Set(["quit", "disconnect"]);

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeTextKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const getMatchPlayers = (matchData = {}) => toArray(matchData?.players).filter(Boolean);

const getMatchMoves = (matchData = {}) => toArray(matchData?.moves).filter(Boolean);

export const getStoryBattleRarityXp = (rarity = "") =>
  STORY_BATTLE_RARITY_XP[normalizeTextKey(rarity)] || 0;

export const isEligibleStoryBattleXpResult = ({
  resultKey = "",
  matchData = {},
} = {}) => {
  if (INELIGIBLE_RESULT_KEYS.has(normalizeTextKey(resultKey))) {
    return false;
  }

  return matchData?.status === "finished" || Boolean(matchData?.winner);
};

const getStorySidePlayerIds = ({ battle = null, matchData = {} } = {}) => {
  const storySideId = String(battle?.storySide?.sideId || "red").trim();

  return new Set(
    getMatchPlayers(matchData)
      .filter((player) => String(player?.team || "").trim() === storySideId)
      .map((player) => String(player?.id || "").trim())
      .filter(Boolean),
  );
};

const getEnemyPlayers = ({ battle = null, matchData = {} } = {}) => {
  const storySideId = String(battle?.storySide?.sideId || "red").trim();

  return getMatchPlayers(matchData).filter(
    (player) => String(player?.team || "").trim() !== storySideId,
  );
};

export const getStoryBattlePlayedCardCount = ({
  battle = null,
  matchData = {},
} = {}) => {
  const storySidePlayerIds = getStorySidePlayerIds({ battle, matchData });
  if (!storySidePlayerIds.size) return 0;

  // Story teammates like Toshi are frequently bot players, so `playerPoints`
  // will not include their card plays. The match move log is the only shared
  // source of truth that includes every actor on the Story side.
  return getMatchMoves(matchData).reduce((count, move) => {
    if (normalizeTextKey(move?.type) !== "playcard") {
      return count;
    }

    return storySidePlayerIds.has(String(move?.actorId || "").trim())
      ? count + 1
      : count;
  }, 0);
};

const hasBossOnOpponentSide = (battle = null) =>
  toArray(battle?.opponentSide?.opponentRoles).some((role) => Boolean(role?.isBoss));

export const buildStoryBattleXpAward = ({
  roomId = "",
  missionId = "",
  encounterId = "",
  battle = null,
  matchData = {},
  resultKey = "",
} = {}) => {
  const claimKey = String(roomId || "").trim() || null;
  const enemyPlayers = getEnemyPlayers({ battle, matchData });
  const storyCardsPlayed = getStoryBattlePlayedCardCount({ battle, matchData });
  const rarityXp = enemyPlayers.reduce(
    (total, player) => total + getStoryBattleRarityXp(player?.character?.rarity),
    0,
  );
  const hasBoss = hasBossOnOpponentSide(battle);
  const bossXp = hasBoss ? STORY_BATTLE_BOSS_XP : 0;
  const cardPlayXp = storyCardsPlayed * STORY_BATTLE_CARD_PLAY_XP;
  const eligible =
    Boolean(claimKey) &&
    isEligibleStoryBattleXpResult({
      resultKey,
      matchData,
    });

  return {
    claimKey,
    missionId: String(missionId || "").trim() || null,
    encounterId: String(encounterId || "").trim() || null,
    totalXp: eligible ? rarityXp + cardPlayXp + bossXp : 0,
    rarityXp,
    cardPlayXp,
    bossXp,
    enemyCount:
      enemyPlayers.length || toArray(battle?.opponentSide?.opponentRoles).length,
    storyCardsPlayed,
    hasBoss,
    eligible,
  };
};
